import { Dialog, DialogActions, DialogContent, Stack, TextField } from "@mui/material";
import { MprcButton, MprcConfirmDialog, MprcContainer, MprcDialogTitleClose, MprcRequestIndicator, MprcStringToHtml } from "components";
import { ButtonCancel, ButtonSave } from "components/buttons";
import { MprcRequestErrorReport, MprcRequestSnackbarErrorReport, MprcResolveRequestErrors } from "components/MprcRequestErrorReport";
import { DevPlanGroupDto, TopicCategory } from "generated/CoreApi_v1";
import LibUtils from "lib/LibUtils";
import { enqueueSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { TopicAutoComplete } from "views/foundation/topic/components/TopicAutoComplete";

////////////////////////////////////////////////////////////////////////////////

interface IValidationFields {
    name?: string[] | null,
    description?: string[] | null,
    topic?: string[] | null;
}

interface IPros {
    devPlanId: string;
    devPlanGroupId: string | null;
    onClose: (success: boolean) => void;
}

/////////////////////////////////////////////////////////////////////////////////

export const EditDevPlanGroupDialog: React.FC<IPros> = (props) => {

    const api = useCoreApi();
    const { t } = useTranslation("lms");

    const { devPlanId, devPlanGroupId, onClose } = props;

    const [dialog, setDialog] = useState<any>();
    const [viewData, setViewData] = useState<DevPlanGroupDto>({} as DevPlanGroupDto);
    const [loadDto, setLoadDto] = useState<DevPlanGroupDto>();
    const [error, setError] = useState<any>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [recordChanged, setRecordChanged] = useState<boolean>(false);
    const [formValidation, setFormValidation] = useState<IValidationFields>({
        name: undefined
    });

    const isAdding = devPlanGroupId == null;
    const canSave = recordChanged && Object.values(formValidation).filter(p => p !== null).length === 0;

    ///
    const handleSave = () => {
        setSaving(true);
        api.v1.devPlan.createOrUpdateDevPlanGroup({
            devPlanId: devPlanId,
            devPlanGroupId: devPlanGroupId ?? null,
            name: viewData.name,
            description: viewData.description,
            topicId: viewData.topic?.key
        })
            .then(response => {
                if (response.data === true) {
                    enqueueSnackbar(t("Record.DeleteSuccessMessage", { ns: "shared" }), { variant: "success" });
                    onClose(true);
                }
                else {
                    enqueueSnackbar(t("Record.DeleteFailMessage", { ns: "shared" }), { variant: "error" });
                }
            })
            .catch(e => {
                const errors = MprcResolveRequestErrors(e);
                const fieldErrors = errors.fieldsWithError;
                if (fieldErrors) {

                    enqueueSnackbar(t("Record.SaveFailedWithFieldErrorsMessage", { ns: "shared" }), { variant: "error" });

                    setFormValidation({
                        ...formValidation,
                        name: fieldErrors["name"] ?? formValidation.name ?? null,
                        description: fieldErrors["description"] ?? formValidation.description ?? null,
                        topic: fieldErrors["topicId"] ?? formValidation.topic ?? null,
                    });
                }
                else {
                    MprcRequestSnackbarErrorReport(e);
                }
            })
            .finally(() => setSaving(false));
    };

    const deleteGroup = () => {
        if(devPlanGroupId == null) return;

        setSaving(true);
        api.v1.devPlan.deleteGroup({ devPlanGroupId: devPlanGroupId! })
            .then(response => {
                if (response.data === true) {
                    enqueueSnackbar(t("Record.DeleteSuccessMessage", { ns: "shared" }), { variant: "success" });
                    onClose(true);
                }
                else {
                    enqueueSnackbar(t("Record.DeleteFailMessage", { ns: "shared" }), { variant: "error" });
                }
            })
            .catch(e => MprcRequestSnackbarErrorReport(e))
            .finally(() => setSaving(false));
    };

    ///
    const handleDelete = () => {
        setDialog(
            <MprcConfirmDialog
                open
                severity="danger"
                message={t("DevPlan.EditGroup.ConfirmDeleteMessage")}
                onClose={(yes) => {
                    if (yes) {
                        if (viewData.itemsCount > 0) {
                            setDialog(
                                <MprcConfirmDialog
                                    open
                                    severity="danger"
                                    message={
                                        <Trans
                                            t={t}
                                            i18nKey={"DevPlan.EditGroup.ConfirmDeleteItemsMessage"}
                                            values={{ count: viewData.itemsCount }}
                                        />
                                    }
                                    onClose={(yes2) => {
                                        if (yes2) {
                                            deleteGroup();
                                        }
                                        setDialog(null);
                                    }}
                                />
                            );
                        }
                        else {
                            deleteGroup();
                        }
                    } else {
                        setDialog(null);
                    }
                }}
            />
        );
    };

    ///
    const validateForm = (data: DevPlanGroupDto) => {
        let newFormValidation = { ...formValidation };
        newFormValidation.name = data.name != undefined && data.name.length == 0 ? [t("Messages.RequiredFieldText", { ns: "shared" })] : null;

        setFormValidation(newFormValidation);
    };

    ///
    const handleUpdateRecord = (name: keyof DevPlanGroupDto, v: any) => {
        const newViewData = { ...viewData, [name]: v };

        validateForm(newViewData);
        setViewData(newViewData);
    };

    useEffect(() => {
        if (devPlanGroupId) {
            setLoading(true);
            api.v1.devPlan.getDevPlanGroup({ devPlanId, devPlanGroupId })
                .then(response => {
                    setViewData(response.data);
                    setLoadDto(response.data);
                })
                .catch(e => setError(e))
                .finally(() => setLoading(false));
        }
    }, [devPlanGroupId]);

    useEffect(() => {
        setRecordChanged(!LibUtils.deepCompare(viewData, loadDto));
    }, [viewData]);


    return (
        <>
            {dialog}
            <MprcRequestIndicator visible={loading || saving} type={saving ? "post" : "request"} />
            <Dialog open fullWidth maxWidth="sm" onClose={() => onClose(false)}>
                <MprcDialogTitleClose
                    title={devPlanGroupId == null ? t("DevPlan.EditGroup.TitleNew") : t("DevPlan.EditGroup.TitleEdit")}
                    onClose={() => onClose(false)}
                    shrinkPadding
                />
                <DialogContent>
                    <MprcRequestErrorReport error={error} />
                    <TextField
                        fullWidth
                        required
                        name="name"
                        autoFocus
                        label={t("DevPlan.EditGroup.NameLabel")}
                        margin="dense"
                        slotProps={{
                            input: {
                                inputProps: { maxLength: 255 }
                            }
                        }}
                        onChange={(e) => handleUpdateRecord("name", e.target.value)}
                        value={viewData.name || ""}
                        error={!!formValidation.name}
                        helperText={!!formValidation.name && <MprcStringToHtml text={formValidation.name} />}
                    />
                    <MprcContainer>
                        <TextField
                            fullWidth
                            name="description"
                            label={t("DevPlan.EditGroup.DescriptionLabel")}
                            margin="dense"
                            multiline
                            slotProps={{
                                input: {
                                    inputProps: { maxLength: 512 }
                                }
                            }}
                            onChange={(e) => handleUpdateRecord("description", e.target.value)}
                            value={viewData.description || ""}
                            error={!!formValidation.description}
                            helperText={!!formValidation.description && <MprcStringToHtml text={formValidation.description} />}
                        />
                    </MprcContainer>
                    <MprcContainer>
                        <TopicAutoComplete
                            categories={[TopicCategory.Competency]}
                            label={t("DevPlan.EditGroup.TopicLabel")}
                            placeholder={t("DevPlan.EditGroup.TopicPlaceholder")}
                            onChange={(items) => handleUpdateRecord("topic", items.length > 0 ? items[0] : null)}
                            selectedItems={viewData.topic ? [viewData.topic] : []}
                            error={!!formValidation.topic}
                            helperText={!!formValidation.topic && <MprcStringToHtml text={formValidation.topic} />}
                        />
                    </MprcContainer>
                </DialogContent>
                <DialogActions>
                    <Stack direction="row" justifyContent={isAdding ? "flex-end" : "space-between"} width="100%">
                        {!isAdding &&
                            <MprcButton
                                variant="contained"
                                color="error"
                                text={t("DevPlan.EditGroup.DeleteButtonText")}
                                onClick={handleDelete}
                            />
                        }
                        <Stack direction="row" spacing={1}>
                            <ButtonCancel variant="contained" onClick={() => onClose(false)} />
                            <ButtonSave
                                disabled={!canSave}
                                variant="contained"
                                onClick={handleSave}
                            />
                        </Stack>
                    </Stack>
                </DialogActions>
            </Dialog>
        </>
    );
};
